import React, {Component} from 'react';
import {
    Paper,
    Typography,
    Box,
    Grid,
    Button,
    Skeleton,
    Divider,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Alert,
    Collapse,
    Card,
    CardContent,
    MenuItem,
    Menu
} from '@mui/material';
import {
    Description as DescriptionIcon,
    InsertDriveFile as FileIcon,
    Download as DownloadIcon,
    Close as CloseIcon,
    MoreVert as MoreVertIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    FilePresent as FileUploadIcon,
    Search as SearchIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
    Assignment as AssignmentIcon,
    AssignmentInd as AssignmentIndIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import {ApiDetails, KEY} from "../../../dummyData";
import CryptoJS from "crypto-js";

// Document type icons mapping
const documentTypeIcons = {
    'lease': <AssignmentIcon sx={{color: '#3B82F6'}}/>,
    'id': <AssignmentIndIcon sx={{color: '#F59E0B'}}/>,
    'other': <FileIcon sx={{color: '#9CA3AF'}}/>
};

// File type icons mapping
const fileTypeIcons = {
    'pdf': <PdfIcon sx={{color: '#EF4444'}}/>,
    'jpg': <ImageIcon sx={{color: '#10B981'}}/>,
    'jpeg': <ImageIcon sx={{color: '#10B981'}}/>,
    'png': <ImageIcon sx={{color: '#10B981'}}/>,
    'doc': <FileIcon sx={{color: '#3B82F6'}}/>,
    'docx': <FileIcon sx={{color: '#3B82F6'}}/>,
    'xls': <FileIcon sx={{color: '#10B981'}}/>,
    'xlsx': <FileIcon sx={{color: '#10B981'}}/>,
    'other': <FileIcon sx={{color: '#9CA3AF'}}/>
};

class Documents extends Component {

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            documents: [],
            previewDialogOpen: false,
            showSuccessAlert: false,
            successMessage: '',
            selectedDocument: null,
            anchorEl: null,
            menuDocumentId: null,
            searchTerm: '',
            filterType: 'all',
            sortBy: 'dateDesc',
            error: null
        };
    }

    componentDidMount() {
        // Fetch user documents
        this.fetchUserDocuments();
    }

    // Helper function to determine document type based on key
    getDocumentType = (key) => {
        // Map document keys to document types used in your UI
        const typeMap = {
            'national_id': 'id',
            'national_id_front': 'id',
            'national_id_back': 'id',
            'tenant_photo': 'id',
            'contract': 'lease',
            'inventory_checklist': 'lease'
        };

        return typeMap[key] || 'other';
    };

    fetchUserDocuments = async () => {
        this.setState({loading: true});

        // Decrypt tenant details from localStorage

        let details = CryptoJS.AES.decrypt(
            localStorage.getItem("tenant_details"),
            KEY
        );
        let originalText = details.toString(CryptoJS.enc.Utf8);

        try {
            // Use the correct endpoint and tenantId from props
            const documentsResponse = await fetch(ApiDetails + "pegasus/visionary/tenant/fetch/documents", {
                method: 'POST',
                headers: {
                    "token": new Map(
                        Object.entries(JSON.parse(originalText))
                    ).get("access_token"),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({tenant_id: this.props.tenantId})
            });

            if (!documentsResponse.ok) {
                throw new Error(`HTTP error! Status: ${documentsResponse.status}`);
            }

            const data = await documentsResponse.json();

            if (data.success) {
                // Transform the documents map into array format expected by the component
                const documentsArray = [];

                // Process each document in the documents map
                Object.entries(data.documents).forEach(([key, doc]) => {
                    // Skip if base64 is empty or missing
                    if (!doc.base64) return;

                    documentsArray.push({
                        id: key, // Use the key as ID (national_id, contract, etc.)
                        name: doc.filename || `${doc.name}.${doc.file_type}`, // Use original filename or create one
                        type: this.getDocumentType(key), // Helper function to determine document type
                        fileType: doc.file_type,
                        uploadDate: new Date().toISOString(), // Since the API doesn't provide this
                        status: 'active',
                        fileData: doc.base64,
                        size: doc.size_kb
                    });
                });

                this.setState({
                    loading: false,
                    documents: documentsArray,
                    error: null
                });
            } else {
                throw new Error(data.message || "Failed to load documents");
            }
        } catch (error) {
            console.error("Error loading documents:", error);
            this.setState({
                loading: false,
                documents: [],
                error: error.message || "Failed to load documents. Please try again later."
            });
        }
    };

    // Open preview dialog
    openPreviewDialog = (document) => {
        this.setState({
            previewDialogOpen: true,
            selectedDocument: document
        });
    };

    // Close preview dialog
    closePreviewDialog = () => {
        this.setState({
            previewDialogOpen: false,
            selectedDocument: null
        });
    };

    // Format upload date
    formatDate = (dateString) => {
        const options = {year: 'numeric', month: 'short', day: 'numeric'};
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    // Handle menu open
    handleMenuOpen = (event, documentId) => {
        this.setState({
            anchorEl: event.currentTarget,
            menuDocumentId: documentId
        });
    };

    // Handle menu close
    handleMenuClose = () => {
        this.setState({
            anchorEl: null,
            menuDocumentId: null
        });
    };

    // Handle document menu actions
    handleDocumentAction = (action) => {
        const {documents, menuDocumentId} = this.state;
        const document = documents.find(doc => doc.id === menuDocumentId);

        this.setState({anchorEl: null});

        switch (action) {
            case 'view':
                this.openPreviewDialog(document);
                break;
            case 'download':
                this.downloadDocument(document);
                break;
            default:
                break;
        }
    };

    // Download document using base64 data
    downloadDocument = (document) => {
        // Determine the correct MIME type
        let mimeType;
        switch (document.fileType.toLowerCase()) {
            case 'pdf':
                mimeType = 'application/pdf';
                break;
            case 'jpg':
            case 'jpeg':
                mimeType = 'image/jpeg';
                break;
            case 'png':
                mimeType = 'image/png';
                break;
            default:
                mimeType = 'application/octet-stream';
        }

        // Create a download link
        const linkSource = `data:${mimeType};base64,${document.fileData}`;
        const downloadLink = document.createElement('a');

        downloadLink.href = linkSource;
        downloadLink.download = document.name;
        downloadLink.click();

        this.setState({
            showSuccessAlert: true,
            successMessage: `Downloading ${document.name}...`
        });

        // Hide success message after 3 seconds
        setTimeout(() => {
            this.setState({showSuccessAlert: false});
        }, 3000);
    };

    // Get file icon based on file type
    getFileIcon = (fileType) => {
        return fileTypeIcons[fileType.toLowerCase()] || fileTypeIcons['other'];
    };

    // Get document type icon
    getDocumentTypeIcon = (documentType) => {
        return documentTypeIcons[documentType] || documentTypeIcons['other'];
    };

    // Get document status chip
    getStatusChip = (status) => {
        switch (status) {
            case 'active':
                return (
                    <Chip
                        icon={<CheckCircleIcon fontSize="small"/>}
                        label="Active"
                        size="small"
                        sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            backgroundColor: '#ECFDF5',
                            color: '#10B981'
                        }}
                    />
                );
            case 'pending':
                return (
                    <Chip
                        icon={<WarningIcon fontSize="small"/>}
                        label="Pending"
                        size="small"
                        sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            backgroundColor: '#FEF3C7',
                            color: '#F59E0B'
                        }}
                    />
                );
            default:
                return (
                    <Chip
                        label={status}
                        size="small"
                        sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            backgroundColor: '#F3F4F6',
                            color: '#6B7280'
                        }}
                    />
                );
        }
    };

    // Handle search term change
    handleSearchChange = (e) => {
        this.setState({searchTerm: e.target.value});
    };

    // Handle filter type change
    handleFilterChange = (filterType) => {
        this.setState({filterType});
    };

    // Handle sort change
    handleSortChange = (sortBy) => {
        this.setState({sortBy});
    };

    // Get filtered and sorted documents
    getFilteredDocuments = () => {
        const {documents, searchTerm, filterType, sortBy} = this.state;

        // First filter by search term
        let filteredDocs = documents.filter(doc =>
            doc.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Then filter by type if not 'all'
        if (filterType !== 'all') {
            filteredDocs = filteredDocs.filter(doc => doc.type === filterType);
        }

        // Finally sort
        switch (sortBy) {
            case 'nameAsc':
                return filteredDocs.sort((a, b) => a.name.localeCompare(b.name));
            case 'nameDesc':
                return filteredDocs.sort((a, b) => b.name.localeCompare(a.name));
            case 'dateAsc':
                return filteredDocs.sort((a, b) => new Date(a.uploadDate) - new Date(b.uploadDate));
            case 'dateDesc':
            default:
                return filteredDocs.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
        }
    };

    // Render document list
    renderDocumentList = () => {
        const {loading, error} = this.state;
        const filteredDocuments = this.getFilteredDocuments();

        if (loading) {
            // Show skeletons while loading
            return Array(3).fill(0).map((_, index) => (
                <ListItem
                    key={index}
                    sx={{
                        mb: 1,
                        borderRadius: '8px',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        overflow: 'hidden'
                    }}
                >
                    <ListItemIcon>
                        <Skeleton variant="circular" width={40} height={40}/>
                    </ListItemIcon>
                    <ListItemText
                        primary={<Skeleton width="60%"/>}
                        secondary={<Skeleton width="30%"/>}
                    />
                    <Skeleton width={80} height={32} sx={{borderRadius: '16px'}}/>
                </ListItem>
            ));
        }

        if (error) {
            return (
                <Box sx={{p: 3}}>
                    <Alert severity="error" sx={{mb: 2}}>
                        {error}
                    </Alert>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={this.fetchUserDocuments}
                        sx={{mt: 2}}
                    >
                        Retry
                    </Button>
                </Box>
            );
        }

        if (filteredDocuments.length === 0) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                        px: 2,
                        textAlign: 'center'
                    }}
                >
                    <FileUploadIcon sx={{fontSize: 64, color: '#E5E7EB', mb: 2}}/>
                    <Typography variant="h6" color="textSecondary">
                        No documents found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                        {this.state.searchTerm || this.state.filterType !== 'all'
                            ? 'Try changing your search or filter criteria'
                            : ''}
                    </Typography>
                </Box>
            );
        }

        return (
            <List sx={{width: '100%', mt: 2}}>
                {filteredDocuments.map(document => (
                    <Card
                        key={document.id}
                        sx={{
                            mb: 2,
                            borderRadius: '8px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            transition: 'box-shadow 0.3s ease',
                            '&:hover': {
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }
                        }}
                    >
                        <CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 40,
                                        height: 40,
                                        borderRadius: '8px',
                                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                        mr: 2
                                    }}
                                >
                                    {this.getFileIcon(document.fileType)}
                                </Box>

                                <Box sx={{flexGrow: 1}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', mb: 0.5}}>
                                        <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                            {document.name}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                ml: 2
                                            }}
                                        >
                                            <Chip
                                                icon={this.getDocumentTypeIcon(document.type)}
                                                label={document.type.charAt(0).toUpperCase() + document.type.slice(1)}
                                                size="small"
                                                sx={{
                                                    height: 24,
                                                    fontSize: '0.75rem',
                                                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                                    color: '#3B82F6',
                                                    mr: 1
                                                }}
                                            />
                                            {this.getStatusChip(document.status)}
                                        </Box>
                                    </Box>

                                    <Typography variant="caption" color="textSecondary">
                                        Uploaded on {this.formatDate(document.uploadDate)}
                                        {document.size && ` • ${document.size} KB`}
                                    </Typography>
                                </Box>

                                <Box>
                                    <IconButton onClick={(e) => this.handleMenuOpen(e, document.id)} size="small">
                                        <MoreVertIcon fontSize="small"/>
                                    </IconButton>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </List>
        );
    };

    render() {
        const {
            previewDialogOpen,
            selectedDocument,
            showSuccessAlert,
            successMessage,
            anchorEl,
            menuDocumentId
        } = this.state;
        const {className} = this.props;

        const open = Boolean(anchorEl);

        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden'
                }}
                className={`documents-tab ${className || ''}`}
            >
                {/* Success Alert */}
                <Collapse in={showSuccessAlert}>
                    <Alert
                        severity="success"
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => this.setState({showSuccessAlert: false})}
                            >
                                <CloseIcon fontSize="inherit"/>
                            </IconButton>
                        }
                        sx={{borderRadius: 0}}
                    >
                        {successMessage}
                    </Alert>
                </Collapse>

                {/* Header Section */}
                <Box sx={{p: 3, pb: 2}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: '#1F2937',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <DescriptionIcon sx={{color: '#3B82F6'}}/>
                            My Documents
                        </Typography>
                    </Box>

                    <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                        Manage your important documents and files
                    </Typography>

                    <Divider/>
                </Box>

                {/* Filter and Search Section */}
                <Box sx={{px: 3, pt: 1, pb: 2}}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={4}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(0, 0, 0, 0.12)',
                                    px: 1.5,
                                    py: 0.5
                                }}
                            >
                                <SearchIcon sx={{color: '#9CA3AF', mr: 1}}/>
                                <input
                                    type="text"
                                    placeholder="Search documents..."
                                    value={this.state.searchTerm}
                                    onChange={this.handleSearchChange}
                                    style={{
                                        border: 'none',
                                        outline: 'none',
                                        width: '100%',
                                        fontSize: '0.875rem',
                                        padding: '8px 0'
                                    }}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={8}>
                            <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1}}>
                                <Box>
                                    <Button
                                        variant={this.state.filterType === 'all' ? 'contained' : 'outlined'}
                                        color="primary"
                                        size="small"
                                        onClick={() => this.handleFilterChange('all')}
                                        sx={{
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            boxShadow: 'none',
                                            minWidth: 'auto'
                                        }}
                                    >
                                        All
                                    </Button>
                                </Box>

                                {Object.keys(documentTypeIcons).filter(type => type !== 'other').map(type => (
                                    <Box key={type}>
                                        <Button
                                            variant={this.state.filterType === type ? 'contained' : 'outlined'}
                                            color="primary"
                                            size="small"
                                            startIcon={documentTypeIcons[type]}
                                            onClick={() => this.handleFilterChange(type)}
                                            sx={{
                                                borderRadius: '8px',
                                                textTransform: 'none',
                                                boxShadow: 'none'
                                            }}
                                        >
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </Button>
                                    </Box>
                                ))}

                                <Box>
                                    <Tooltip title="Sort options">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => this.setState({
                                                anchorEl: e.currentTarget,
                                                menuDocumentId: 'sort'
                                            })}
                                            sx={{ml: 1}}
                                        >
                                            <SettingsIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Documents List Section */}
                <Box sx={{px: 3, pb: 3}}>
                    {this.renderDocumentList()}
                </Box>

                {/* Preview Dialog */}
                {previewDialogOpen && selectedDocument && (
                    <Dialog
                        open={previewDialogOpen}
                        onClose={this.closePreviewDialog}
                        maxWidth="md"
                        fullWidth
                        PaperProps={{
                            sx: {
                                borderRadius: '12px',
                                overflow: 'hidden'
                            }
                        }}
                    >
                        <DialogTitle sx={{
                            py: 2,
                            px: 3,
                            backgroundColor: '#f8fafc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                {this.getFileIcon(selectedDocument.fileType)}
                                <Typography sx={{ml: 1}}>
                                    {selectedDocument.name}
                                </Typography>
                            </Box>
                            <IconButton onClick={this.closePreviewDialog} size="small">
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        </DialogTitle>

                        <DialogContent sx={{p: 0, minHeight: '60vh', display: 'flex', flexDirection: 'column'}}>
                            <Box sx={{p: 2, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="textSecondary">
                                            Document Type
                                        </Typography>
                                        <Typography variant="body2"
                                                    sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                            {this.getDocumentTypeIcon(selectedDocument.type)}
                                            {selectedDocument.type.charAt(0).toUpperCase() + selectedDocument.type.slice(1)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="textSecondary">
                                            Upload Date
                                        </Typography>
                                        <Typography variant="body2">
                                            {this.formatDate(selectedDocument.uploadDate)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="textSecondary">
                                            Status
                                        </Typography>
                                        <Box sx={{mt: 0.5}}>
                                            {this.getStatusChip(selectedDocument.status)}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="textSecondary">
                                            File Type
                                        </Typography>
                                        <Typography variant="body2" sx={{textTransform: 'uppercase'}}>
                                            {selectedDocument.fileType}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box sx={{
                                flexGrow: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 3,
                                backgroundColor: '#f1f5f9'
                            }}>
                                {selectedDocument.fileType.toLowerCase() === 'pdf' ? (
                                    <object
                                        data={`data:application/pdf;base64,${selectedDocument.fileData}`}
                                        type="application/pdf"
                                        width="100%"
                                        height="500px"
                                    >
                                        <Box sx={{textAlign: 'center'}}>
                                            <PdfIcon sx={{fontSize: 64, color: '#EF4444', mb: 2}}/>
                                            <Typography variant="body2" color="textSecondary">
                                                PDF viewer not supported by your browser
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<DownloadIcon/>}
                                                onClick={() => this.downloadDocument(selectedDocument)}
                                                sx={{mt: 2, borderRadius: '8px', textTransform: 'none'}}
                                            >
                                                Download to view
                                            </Button>
                                        </Box>
                                    </object>
                                ) : ['jpg', 'jpeg', 'png'].includes(selectedDocument.fileType.toLowerCase()) ? (
                                    <img
                                        src={`data:image/${selectedDocument.fileType};base64,${selectedDocument.fileData}`}
                                        alt={selectedDocument.name}
                                        style={{maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain'}}
                                    />
                                ) : (
                                    <Box sx={{textAlign: 'center'}}>
                                        {this.getFileIcon(selectedDocument.fileType)}
                                        <Typography variant="body2" color="textSecondary" sx={{mt: 2}}>
                                            Preview not available
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </DialogContent>

                        <DialogActions
                            sx={{py: 2, px: 3, borderTop: '1px solid #e2e8f0', justifyContent: 'flex-end'}}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<DownloadIcon/>}
                                onClick={() => this.downloadDocument(selectedDocument)}
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    boxShadow: 'none'
                                }}
                            >
                                Download
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}

                {/* Document Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={this.handleMenuClose}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            borderRadius: '8px',
                            minWidth: 150
                        }
                    }}
                    transformOrigin={{horizontal: 'right', vertical: 'top'}}
                    anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                >
                    {menuDocumentId === 'sort' ? (
                        <>
                            <MenuItem
                                onClick={() => {
                                    this.handleSortChange('dateDesc');
                                    this.handleMenuClose();
                                }}
                                sx={{py: 1, px: 2}}
                            >
                                <ListItemIcon>
                                    <DescriptionIcon fontSize="small" sx={{color: '#3B82F6'}}/>
                                </ListItemIcon>
                                <ListItemText>Newest first</ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    this.handleSortChange('dateAsc');
                                    this.handleMenuClose();
                                }}
                                sx={{py: 1, px: 2}}
                            >
                                <ListItemIcon>
                                    <DescriptionIcon fontSize="small" sx={{color: '#3B82F6'}}/>
                                </ListItemIcon>
                                <ListItemText>Oldest first</ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    this.handleSortChange('nameAsc');
                                    this.handleMenuClose();
                                }}
                                sx={{py: 1, px: 2}}
                            >
                                <ListItemIcon>
                                    <DescriptionIcon fontSize="small" sx={{color: '#3B82F6'}}/>
                                </ListItemIcon>
                                <ListItemText>Name (A-Z)</ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    this.handleSortChange('nameDesc');
                                    this.handleMenuClose();
                                }}
                                sx={{py: 1, px: 2}}
                            >
                                <ListItemIcon>
                                    <DescriptionIcon fontSize="small" sx={{color: '#3B82F6'}}/>
                                </ListItemIcon>
                                <ListItemText>Name (Z-A)</ListItemText>
                            </MenuItem>
                        </>
                    ) : (
                        <>
                            <MenuItem
                                onClick={() => this.handleDocumentAction('view')}
                                sx={{py: 1, px: 2}}
                            >
                                <ListItemIcon>
                                    <VisibilityIcon fontSize="small" sx={{color: '#3B82F6'}}/>
                                </ListItemIcon>
                                <ListItemText>View</ListItemText>
                            </MenuItem>
                        </>
                    )}
                </Menu>
            </Paper>
        );
    }
}

export default Documents;